/**
 * PaperFormsTab - Settings tab offering printable blank prescription forms
 * for hand filling in clinic (later transcribed into the order wizard).
 *
 * Three PDFs, one per order family: custom insoles (superset across every
 * shell material currently enabled in the catalog), orthopaedic sandals and
 * insoles refurbishment. Forms are generated client-side on click from the
 * SAME option constants the wizard renders, so paper and system never drift.
 * They carry no patient data: one printout works for any patient and
 * photocopies freely.
 */
import * as React from 'react';
import { Alert, Button, Card, CardBody } from '@insolech/ui';
import { Printer } from 'lucide-react';
import { downloadPaperForm, type PaperFormVariant } from './downloadPaperForm';

interface FormRow {
  variant: PaperFormVariant;
  title: string;
  description: string;
}

const FORMS: ReadonlyArray<FormRow> = [
  {
    variant: 'custom-insoles',
    title: 'Custom insoles',
    description:
      'Every shell material, modification and option on one form. Tick what applies; blank means None.',
  },
  {
    variant: 'sandals',
    title: 'Orthopaedic sandals',
    description:
      'Sandal style configurator plus the sandal-specific prescription sections.',
  },
  {
    variant: 'refurbishment',
    title: 'Insoles refurbishment',
    description: 'Insole length, top cover and refurbishment remarks.',
  },
];

export function PaperFormsTab(): React.ReactElement {
  const [busy, setBusy] = React.useState<PaperFormVariant | null>(null);

  const onDownload = async (variant: PaperFormVariant): Promise<void> => {
    setBusy(variant);
    try {
      await downloadPaperForm(variant);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <div>
          <p className="text-sm text-text-secondary">
            Print blank prescription forms to fill by hand in clinic, then
            transcribe into a New order. Each form mirrors the order wizard
            field by field and contains no patient data, so one printout works
            for any patient and can be photocopied.
          </p>
        </div>

        <Alert tone="info" title="Always print fresh">
          Forms are generated from the live catalog at download time. Reprint
          after product changes so paper never lags the system.
        </Alert>

        <ul className="flex flex-col divide-y divide-border-subtle">
          {FORMS.map((form) => (
            <li key={form.variant} className="flex items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-text-primary">{form.title}</p>
                <p className="text-xs text-text-muted mt-0.5">{form.description}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                startIcon={<Printer className="h-4 w-4" />}
                onClick={() => void onDownload(form.variant)}
                disabled={busy !== null}
              >
                {busy === form.variant ? 'Generating…' : 'Download PDF'}
              </Button>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  );
}
